import { useEffect, useCallback, useRef } from 'react';
import { get } from 'lodash';

const useClickOutside = (ref, callbackFn) => {
  const checkForOutsideClick = event => {
    let isClickedOutside = false;

    if (ref.current) {
      const dimmerClassName = get(
        ref.current,
        'dimmerRef.current.className',
        '',
      );
      const className = get(event.target, 'className');

      if (!!dimmerClassName && !!className)
        isClickedOutside = dimmerClassName === className;
    }

    return isClickedOutside;
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (checkForOutsideClick(event)) callbackFn();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, callbackFn]);
};

export const useClickOutSideGen = (ref, callbackFn, ...extraRef) => {
  const checkForOutsideClick = event => {
    let isClickedOutside = true;

    if (ref.current && ref.current.contains(event.target)) {
      isClickedOutside = false;
    }

    extraRef.forEach(eref => {
      if (eref.current && eref.current.contains(event.target)) {
        isClickedOutside = false;
      }
    });

    return isClickedOutside;
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (checkForOutsideClick(event)) {
        callbackFn();
      }
    };

    document.addEventListener('mousedown', handleClickOutside, {
      capture: true,
    });

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, {
        capture: true,
      });
    };
  }, [ref, callbackFn]);
};

export const useClickOutSideOfMultipleRefs = (refs, callback) => {
  const handleClick = e => {
    let isClickedInside = false;
    refs.forEach(ref => {
      isClickedInside =
        isClickedInside || (ref.current && ref.current.contains(e.target));
    });
    if (!isClickedInside) {
      callback();
    }
  };
  useEffect(() => {
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  });
};

export const useKeyboardShortcut = (shortcutKeys, callback, node = null) => {
  const handleKeydown = useCallback(
    event => {
      // condition is to check
      // - if the one of the shortcut key is pressed
      // - if shift key is pressed
      // - if metaKey/command key or ctrlKey is pressed
      if (
        shortcutKeys[event.key?.toUpperCase && event.key.toUpperCase()] &&
        event.shiftKey &&
        (event.metaKey || event.ctrlKey)
      ) {
        callback(shortcutKeys[event.key.toUpperCase()]);
        event.preventDefault();
      }
    },
    [shortcutKeys, callback],
  );

  useEffect(() => {
    const targetNode = node || document;
    targetNode.addEventListener('keydown', handleKeydown);

    return () => {
      targetNode.removeEventListener('keydown', handleKeydown);
    };
  }, [handleKeydown, node]);
};

export const usePrevious = val => {
  const ref = useRef();

  useEffect(() => {
    ref.current = val;
  }, [val]);

  return ref.current;
};

export default useClickOutside;
